/**
 * Responsibility: one search result set as a table.
 *
 * Ordered by the hub and never re-sorted here, for the same reason the
 * discovery board is not: the ranking is the answer to the mode the operator
 * chose, and a click-to-sort that discarded it would quietly turn "arbitrage"
 * back into "biggest multiple", which is how a 31 cent common that slabs at
 * $31.55 ends up on top of a list of cards that actually make money.
 *
 * The columns are the discovery board's columns, minus the ones search
 * cannot answer, because the two tables are read one after the other and a
 * heading that means one thing here and another there costs more than the
 * space it saves. How thin the evidence behind a price is, and how often the
 * grade trades at all, are warnings the card dialog carries - they come off
 * the product page, which search does not read.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import { EMPTY } from '@kit/utils';
import type { MarketResult } from '../../api/client';
import { useState } from 'react';
import Box from '@mui/material/Box';
import { NameSortHeader } from '../shelf/NameSortHeader';
import { headerNode } from '../shelf/headerNode';
import { nameSortValue } from '../shelf/nameSort';
import type { NameSort } from '../shelf/nameSort';
import { CardThumb } from '../grading/CardThumb';
import { PriceAgeBadge } from './PriceAgeBadge';
import { daysOld } from './priceAge';
import { compactMoney, compactSigned, money, multiple, toNumber } from '../grading/money';

type MarketTableProps = {
  rows: MarketResult[];
  loading?: boolean;
  busy?: boolean;
  onOpen: (row: MarketResult) => void;
  onAdd: (row: MarketResult) => void;
  onLike: (row: MarketResult) => void;
  /** Track a card, as opposed to keeping it to look at again. */
  onWatch: (row: MarketResult) => void;
  /** Keys already liked, so the heart renders filled. */
  liked: Set<string>;
  watched: Set<string>;
  maxHeight?: number;
};

/** Effectively no cap on phones, without patching the kit. */
const UNCAPPED = 100_000;

/** Matches the discovery board, so the two tables read as one system. */
const THUMB_WIDTH = 58;

function NameCell({ row }: { row: MarketResult }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, py: 0.5 }}>
      {/* The picture is the fastest way to tell four printings of the same
          name apart, which is most of what a search for "Ohtani rookie"
          hands back. */}
      <CardThumb src={row.image_url} alt={row.name} width={THUMB_WIDTH} />
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.name}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={row.category === 'SPORTS' ? 'Sports' : 'Trading card'}
          sx={{ alignSelf: 'start' }}
        />
      </Stack>
    </Stack>
  );
}

/**
 * A money figure as a sortable number, with absent sorting last.
 *
 * Absent is not zero. A card nobody has priced is unknown, and sorting it
 * alongside the free ones puts the least informative rows at the top of a
 * column somebody opened to find the dearest.
 */
function sortable(amount: string | null): number {
  if (amount === null) return Number.NEGATIVE_INFINITY;
  const value = Number(amount);
  return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
}

/**
 * The set, and a warning when its prices are old.
 *
 * Search answers from the catalogue, so a figure is as old as the last crawl
 * of that set rather than as old as the request. Saying which is the
 * difference between a stale price and a wrong one.
 */
function SetCell({ row }: { row: MarketResult }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      {/* **The badge replaces the dash rather than sitting under it.**
          A card with no set name showed an em dash with "age unknown"
          beneath it - two rows of nothing where one would do, and the
          dash was the less useful half. */}
      {row.set_name ? (
        <Typography variant="body2" sx={{ minWidth: 0 }} noWrap title={row.set_name}>
          {row.set_name}
        </Typography>
      ) : null}
      <Box sx={{ alignSelf: 'start' }}>
        <PriceAgeBadge days={daysOld(row.priced_at)} />
      </Box>
    </Stack>
  );
}


/** A price with the grade it belongs to, and today's movement. */
function ValueCell({ row }: { row: MarketResult }) {
  const change = toNumber(row.change);
  return (
    <Stack spacing={0} sx={{ minWidth: 0, textAlign: 'right' }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {compactMoney(row.best_price)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {row.best_label ?? EMPTY}
        {change !== null ? (
          <Typography
            component="span"
            variant="caption"
            color={change >= 0 ? 'success.main' : 'error.main'}
          >
            {' '}
            {compactSigned(row.change)}
          </Typography>
        ) : null}
      </Typography>
    </Stack>
  );
}

function ProfitCell({ row }: { row: MarketResult }) {
  const amount = toNumber(row.best_case_profit);
  if (amount === null) {
    return (
      <Typography variant="body2" color="text.disabled">
        {EMPTY}
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      color={amount > 0 ? 'success.main' : 'error.main'}
    >
      {compactSigned(row.best_case_profit)}
    </Typography>
  );
}

export function MarketTable({
  rows,
  loading = false,
  busy = false,
  onOpen,
  onAdd,
  onLike,
  liked,
  watched,
  onWatch,
  maxHeight = 640,
}: MarketTableProps) {
  // Which of the two name orderings the Card column uses. Kept here
  // rather than in the page, because it is a property of this table.
  const [nameSort, setNameSort] = useState<NameSort>('alphabetical');
  const columns: DataColumn<MarketResult>[] = [
    {
      id: 'name',
      label: headerNode(
        <NameSortHeader label="Card" value={nameSort} onChange={setNameSort} />,
      ),
      render: (row) => <NameCell row={row} />,
      sortValue: (row) => nameSortValue(row.name, nameSort),
    },
    {
      id: 'set',
      label: 'Set',
      width: 200,
      sortValue: (row) => (row.set_name ?? '').toLocaleLowerCase(),
      // Its own column rather than a second line under the name: "Charizard
      // #4" is printed by several sets, and which one decides whether the
      // card is worth four figures or four pounds. Sorting and scanning a
      // column of them is the point.
      render: (row) => <SetCell row={row} />,
    },
    {
      id: 'raw',
      sortValue: (row: MarketResult) => sortable(row.raw_price),
      // Headings are set uppercase at 0.7rem in a fixed-width column, so
      // they have about eight characters before they wrap. None of the
      // others say "cost", so this one does not need to either.
      label: 'Raw',
      width: 100,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          color={row.raw_price === null ? 'text.disabled' : 'text.primary'}
        >
          {row.raw_price === null ? EMPTY : money(row.raw_price)}
        </Typography>
      ),
    },
    {
      id: 'value',
      sortValue: (row: MarketResult) => sortable(row.best_price),
      label: 'Best grade',
      // Wide enough for the heading on one line with its sort arrow,
      // measured rather than guessed - at 118 it was a pixel short and
      // wrapped, which is what put the header row on two lines.
      width: 126,
      align: 'right',
      render: (row) => <ValueCell row={row} />,
    },
    {
      id: 'multiple',
      sortValue: (row: MarketResult) => sortable(row.multiple),
      label: 'Mult',
      // Measured, not guessed: at 80 the heading was clipped by five
      // pixels at every window width - the sort arrow takes eighteen of
      // the column and the padding thirty-two.
      width: 92,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.multiple ? multiple(row.multiple) : EMPTY}
        </Typography>
      ),
    },
    {
      id: 'profit',
      sortValue: (row: MarketResult) => sortable(row.best_case_profit),
      label: 'If gems',
      width: 106,
      align: 'right',
      render: (row) => <ProfitCell row={row} />,
    },
    // **Sales and Trades are gone.** Both are read from a product page,
    // and search answers from the catalogue now - so both were empty on
    // every row, one of them showing a dash and the other a nought. The
    // card dialog reads the page and shows them there, where they exist.
    {
      id: 'actions',
      label: '',
      width: 132,
      align: 'right',
      render: (row) => (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
          <ToolbarIconButton
            aria-label={liked.has(row.key) ? 'Remove from likes' : 'Like, to watch it later'}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onLike(row);
            }}
          >
            {liked.has(row.key) ? (
              <FavoriteIcon fontSize="small" color="error" />
            ) : (
              <FavoriteBorderIcon fontSize="small" />
            )}
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label={
              watched.has(row.key) ? 'Stop watching this card' : 'Watch this card'
            }
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onWatch(row);
            }}
          >
            {watched.has(row.key) ? (
              <VisibilityIcon fontSize="small" color="primary" />
            ) : (
              <VisibilityOutlinedIcon fontSize="small" />
            )}
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Add to my collection"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onAdd(row);
            }}
          >
            <AddIcon fontSize="small" />
          </ToolbarIconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={1} sx={{ minHeight: 0, flexGrow: 1 }}>
      <DataTable
        rows={rows}
        columns={columns}
        loading={loading}
        rowKey={(row) => row.key}
        onRowClick={onOpen}
        maxHeight={maxHeight || UNCAPPED}
        emptyMessage="Nothing matched. Try fewer words, or widen the filters."
      />
    </Stack>
  );
}
